/**
 * Navigation Coordinator
 * 
 * Single source of truth for all navigation in OilWellX.
 * Reads user preferences and routes to appropriate navigation method.
 * 
 * This module consolidates navigation logic that was previously scattered across:
 * - App.tsx (handleNavigateToWell, openNavigation)
 * - WellDetail.tsx
 * - WellsListWithLocation.tsx
 * - VoiceNavigation.tsx
 * - RoutesMobile.tsx
 */

import { openExternalMapsApp, generateMapsUrl, getPlatform, isValidDestination } from '../navigation';
import type { MapsApp } from '../../components/MapsAppSelectionDialog';
import { toast } from 'sonner';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface NavigationTarget {
    latitude: number;
    longitude: number;
    name?: string;
    wellId?: string;
    address?: string;
}

export interface UserNavigationPreferences {
    navigationMode: 'internal' | 'external';
    defaultMapsApp?: MapsApp;
}

export interface NavigationRoute {
    id: string;
    name: string;
    wells: Array<{
        wellId: string;
        wellName: string;
        order: number;
        coordinates: { lat: number; lng: number };
        estimatedTime?: number;
        location?: string;
    }>;
    totalDistance?: number;
    estimatedDuration?: number;
    optimized?: boolean;
    status?: string;
}

export interface NavigationCallbacks {
    /** Called when internal navigation should start */
    onStartInternalNavigation: (route: NavigationRoute) => void;
}

export interface NavigationResult {
    success: boolean;
    method: 'internal' | 'external' | 'fallback';
    error?: string;
}

// ============================================================================
// MAIN NAVIGATION FUNCTION
// ============================================================================

/**
 * Unified navigation entry point
 * 
 * Reads user preferences and decides whether to:
 * 1. Use in-app navigation (internal)
 * 2. Open external maps app (mobile)
 * 3. Open Google Maps in browser (desktop web)
 * 
 * @param target - The destination to navigate to
 * @param preferences - User's navigation preferences
 * @param callbacks - Callbacks for internal navigation
 * @returns NavigationResult indicating success/failure and method used
 */
export async function startNavigation(
    target: NavigationTarget,
    preferences: UserNavigationPreferences,
    callbacks: NavigationCallbacks
): Promise<NavigationResult> {

    const { navigationMode, defaultMapsApp } = preferences;
    const platform = getPlatform();


    // Validate destination
    if (!isValidDestination({ latitude: target.latitude, longitude: target.longitude })) {
        console.error('   ❌ Invalid destination coordinates');
        toast.error('Invalid destination coordinates');
        return { success: false, method: 'internal', error: 'Invalid coordinates' };
    }

    // =========================================================================
    // INTERNAL NAVIGATION MODE
    // =========================================================================
    if (navigationMode === 'internal') {

        const route: NavigationRoute = {
            id: `nav-${Date.now()}`,
            name: `Navigate to ${target.name || 'Destination'}`,
            wells: [{
                wellId: target.wellId || 'unknown',
                wellName: target.name || 'Destination',
                order: 1,
                coordinates: { lat: target.latitude, lng: target.longitude },
                estimatedTime: 0.5,
                location: target.address,
            }],
            totalDistance: 0,
            estimatedDuration: 0.5,
            optimized: false,
            status: 'active',
        };

        callbacks.onStartInternalNavigation(route);

        return { success: true, method: 'internal' };
    }

    // =========================================================================
    // EXTERNAL NAVIGATION MODE
    // =========================================================================

    // Check if default maps app is configured
    if (!defaultMapsApp) {
        console.error('   ❌ No default maps app configured');
        toast.error('No default maps app selected', {
            description: 'Please configure in Settings > Navigation or User Profile > Preferences'
        });
        return { success: false, method: 'external', error: 'No maps app configured' };
    }


    // On DESKTOP WEB - always open in browser tab (no native apps available)
    if (platform === 'web') {

        try {
            const webUrl = generateMapsUrl(
                {
                    latitude: target.latitude,
                    longitude: target.longitude,
                    name: target.name,
                    address: target.address
                },
                'system', // Use system default for web
                'web'
            );

            window.open(webUrl, '_blank');

            toast.success('Opened navigation in browser', {
                description: 'Google Maps opened in a new tab'
            });

            return { success: true, method: 'external' };
        } catch (error: any) {
            console.error('   ❌ Failed to open web navigation:', error);
            toast.error('Failed to open navigation');
            return { success: false, method: 'external', error: error.message };
        }
    }

    // On MOBILE (iOS/Android) - try to open native app

    try {
        const success = await openExternalMapsApp(
            {
                latitude: target.latitude,
                longitude: target.longitude,
                name: target.name,
                address: target.address
            },
            defaultMapsApp
        );

        if (success) {
            const appName = getAppDisplayName(defaultMapsApp);
            toast.success(`Opened navigation in ${appName}`);
            return { success: true, method: 'external' };
        }

        // Native app failed - fallback to web
        const fallbackUrl = generateMapsUrl(
            {
                latitude: target.latitude,
                longitude: target.longitude,
                name: target.name,
                address: target.address
            },
            'system',
            'web'
        );

        window.open(fallbackUrl, '_blank');
        toast.success('Opened navigation in browser', {
            description: 'Native app not available, using web fallback'
        });

        return { success: true, method: 'fallback' };
    } catch (error: any) {
        console.error('   ❌ External navigation failed:', error);
        toast.error('Failed to open navigation');
        return { success: false, method: 'external', error: error.message };
    }
}

// ============================================================================
// ROUTE NAVIGATION
// ============================================================================

/**
 * Start navigation for a multi-stop route
 * 
 * For internal navigation: passes the entire route to the in-app navigator
 * For external navigation: opens navigation to the first stop
 * 
 * @param route - The route to navigate
 * @param preferences - User's navigation preferences
 * @param callbacks - Callbacks for internal navigation
 */
export async function startRouteNavigation(
    route: NavigationRoute,
    preferences: UserNavigationPreferences,
    callbacks: NavigationCallbacks
): Promise<NavigationResult> {


    if (!route.wells || route.wells.length === 0) {
        toast.error('No destinations in route');
        return { success: false, method: 'internal', error: 'No destinations' };
    }

    const { navigationMode } = preferences;

    // Internal navigation can handle multi-stop routes
    if (navigationMode === 'internal') {
        callbacks.onStartInternalNavigation(route);
        return { success: true, method: 'internal' };
    }

    // External navigation - navigate to first stop
    // (External apps don't support multi-stop routes via URL schemes)
    const firstStop = route.wells[0];

    if (route.wells.length > 1) {
        toast.info(`External navigation opened to first stop. ${route.wells.length - 1} more stops in route.`);
    }

    return startNavigation(
        {
            latitude: firstStop.coordinates.lat,
            longitude: firstStop.coordinates.lng,
            name: firstStop.wellName,
            wellId: firstStop.wellId,
            address: firstStop.location,
        },
        preferences,
        callbacks
    );
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get display name for maps app
 */
function getAppDisplayName(app: MapsApp): string {
    switch (app) {
        case 'google': return 'Google Maps';
        case 'apple': return 'Apple Maps';
        case 'waze': return 'Waze';
        case 'system': return 'default maps app';
        default: return 'maps app';
    }
}

/**
 * Create a single-well navigation route from a well object
 */
export function createWellNavigationRoute(well: {
    id: string;
    name: string;
    latitude?: number;
    longitude?: number;
    location?: string;
    coordinates?: { lat: number; lng: number };
}): NavigationRoute | null {

    // Try to get coordinates from different possible locations
    let lat: number | undefined;
    let lng: number | undefined;

    if (well.coordinates) {
        lat = well.coordinates.lat;
        lng = well.coordinates.lng;
    } else if (well.latitude !== undefined && well.longitude !== undefined) {
        lat = well.latitude;
        lng = well.longitude;
    } else if (well.location && well.location.includes(',')) {
        // Try to parse "lat,lng" format
        const [latStr, lngStr] = well.location.split(',').map(s => s.trim());
        lat = parseFloat(latStr);
        lng = parseFloat(lngStr);
    }

    if (lat === undefined || lng === undefined || isNaN(lat) || isNaN(lng)) {
        console.error('Unable to determine coordinates for well:', well.name);
        return null;
    }

    return {
        id: `well-${well.id}`,
        name: `Navigate to ${well.name}`,
        wells: [{
            wellId: well.id,
            wellName: well.name,
            order: 1,
            coordinates: { lat, lng },
            estimatedTime: 0.5,
            location: well.location,
        }],
        totalDistance: 0,
        estimatedDuration: 0.5,
        optimized: false,
        status: 'active',
    };
}

/**
 * Extract navigation preferences from user object
 */
export function getUserNavigationPreferences(user: any): UserNavigationPreferences {
    return {
        navigationMode: user?.preferences?.navigationMode || 'internal',
        defaultMapsApp: user?.preferences?.defaultMapsApp,
    };
}
